/**
 * One role's candidates who are already in flight — merged across every round.
 *
 * ── Why this is shared rather than three lists ───────────────────────────────────────────────
 *
 * PR #549 gave each scheduling tab its own collapsed list: "Already invited" on the opening
 * round, "Scheduled" on the case tab, "Booked" on the final round. Each was right about its own
 * round and silent about the other two, so the answer to "who does this role have in flight"
 * depended on which tab you happened to be standing on — and on the Feedback tab, whose cards
 * are defined as *submitted feedback awaiting a decision*, the answer was nothing at all. A role
 * with nine people mid-interview read as a role with one candidate (Theresa, Oct 2026).
 *
 * So the list is assembled once, from the whole role, and the same strip renders at the end of
 * every role card. The ROUND is a tag on each row instead of the name of the section, which is
 * what makes one list readable where three were needed before.
 *
 * ── What it is not ───────────────────────────────────────────────────────────────────────────
 *
 * Not work, and not a send path. Re-inviting a candidate who never booked still belongs to the
 * SLA tab's expired-link flow, which owns the one-second-chance rule. The single action here is
 * the case tab's Lever retry, which is carried over because a booking Lever never heard about
 * is the one state in the list that genuinely needs a person.
 */
import type { RoleBoard } from '../../types/taActionBoard';

/** Which round a row came from. Ordered: a candidate can only be in the latest one. */
export type PipelineRound = 'first' | 'case' | 'final';

export interface PipelineRow {
  opportunityId: string;
  name: string;
  leverUrl: string;
  round: PipelineRound;
  stageName: string;
  /**
   * `invited` — the email left, nothing booked; waiting on the candidate.
   * `booked`  — a slot is held. `inLever: false` is real work: no panel means no scorecard.
   * `arranged`— Lever holds an interview this board did not book.
   */
  state: 'invited' | 'booked' | 'arranged';
  start: string | null;
  past: boolean;
  inLever: boolean;
  /** Opening-round rows only — the board knows who sent the invitation and when. */
  invitedAt: string | null;
  invitedBy: string | null;
  /** Final rounds name their panel. */
  interviewers: string[];
  /** Case rows only: 'onsite' | 'remote'. */
  mode: string | null;
  /** Case rows only — the retry needs it, and only this round has one. */
  retryable: boolean;
  /**
   * Booked opening-round rows: the handle the Reschedule button acts on. Null where the board
   * cannot reschedule yet (case and final rounds, arranged-in-Lever and invited rows).
   */
  bookingRef: string | null;
  /** The interviewer said no-show in Lever, or dismissed the feedback request. */
  noShow: { kind: 'reported' | 'dismissed'; by: string | null } | null;
  /** A no-show was already handled: a new link went out for this booking. */
  noShowAt: string | null;
  /**
   * Earlier interviews booked for this round, newest first — the attempt that was missed or
   * cancelled before the one in `start`. A rebooked candidate used to show one date, with
   * nothing to say there had been a first (Theresa, Oct 22 2026).
   */
  earlier: { start: string; noShow: boolean }[];
}

/** Short tag on the row, so a merged list still says which round each person is in. */
export const ROUND_LABEL: Record<PipelineRound, string> = {
  first: 'Opening',
  case: 'Case',
  final: 'Final',
};

/**
 * Has this payload been built by a proxy that knows about these lists at all?
 *
 * The three fields are optional, and an older cached board simply has none of them — which is
 * indistinguishable from "this role has nobody in flight" unless it is asked separately. That
 * ambiguity is exactly what made the empty strip unreadable: the section vanished in both cases
 * and there was nothing on screen to say which. An unknown payload renders nothing; a known one
 * says "nobody" out loud.
 */
export function pipelineKnown(role: RoleBoard | null | undefined): boolean {
  if (!role) return false;
  return (
    Array.isArray(role.firstInterviews?.scheduled) ||
    Array.isArray(role.scheduling?.scheduled) ||
    Array.isArray(role.finalRounds?.scheduled)
  );
}

/**
 * Every candidate this role has in flight, soonest interview first.
 *
 * @param role the board's role
 * @param now  injectable, so `past` is testable without a clock
 */
export function pipelineRows(role: RoleBoard | null | undefined, now = Date.now()): PipelineRow[] {
  if (!role) return [];
  const rows: PipelineRow[] = [];

  for (const c of role.firstInterviews?.scheduled || []) {
    rows.push({
      opportunityId: c.opportunityId,
      name: c.name,
      leverUrl: c.leverUrl,
      round: 'first',
      stageName: c.stageName,
      state: c.state,
      start: c.start,
      past: c.past,
      inLever: c.inLever,
      invitedAt: c.invitedAt,
      invitedBy: c.invitedBy,
      interviewers: [],
      mode: null,
      retryable: false,
      bookingRef: c.state === 'booked' ? (c.bookingRef ?? null) : null,
      noShow: c.noShow ? { kind: c.noShow.kind, by: c.noShow.by } : null,
      noShowAt: c.noShowAt ?? null,
      // Only the opening round tracks earlier attempts — it is the one with a booking link the
      // candidate uses twice.
      earlier: c.earlier ?? [],
    });
  }

  for (const c of role.scheduling?.scheduled || []) {
    rows.push({
      opportunityId: c.opportunityId,
      name: c.name,
      leverUrl: c.leverUrl,
      round: 'case',
      stageName: c.stageName,
      // Older payloads carry board bookings only, and those were all `booked`.
      state: c.state ?? 'booked',
      start: c.start,
      past: c.past,
      inLever: c.inLever,
      invitedAt: null,
      invitedBy: null,
      interviewers: [],
      mode: c.mode,
      /**
       * The one action in the strip — and only on a case this BOARD booked. An `arranged` panel
       * is already in Lever, so a retry there would offer to create a second one.
       */
      retryable: (c.state ?? 'booked') === 'booked',
      bookingRef: null,
      noShow: null,
      noShowAt: null,
      earlier: [],
    });
  }

  for (const c of role.finalRounds?.scheduled || []) {
    const started = Date.parse(c.start);
    rows.push({
      opportunityId: c.opportunityId,
      name: c.name,
      leverUrl: c.leverUrl,
      round: 'final',
      stageName: c.stageName,
      state: 'booked',
      start: c.start,
      // Derived rather than carried: a Lever panel is what puts a candidate on this list, and
      // the payload has never needed a `past` for it.
      past: Number.isFinite(started) ? started < now : false,
      // In Lever by construction — see FinalRoundScheduled.
      inLever: true,
      invitedAt: null,
      invitedBy: null,
      interviewers: c.interviewers || [],
      mode: null,
      retryable: false,
      bookingRef: null,
      noShow: null,
      noShowAt: null,
      earlier: [],
    });
  }

  return sortRows(dedupe(rows));
}

/**
 * A candidate sits in ONE Lever stage, so the three sources cannot legitimately overlap — but a
 * booking document outlives the stage it was made for, and the case list keys on our own record
 * rather than on Lever. Keeping the LATER round means a candidate who has moved on is described
 * by where they are now, never by the round they have already passed.
 */
function dedupe(rows: PipelineRow[]): PipelineRow[] {
  const rank: Record<PipelineRound, number> = { first: 0, case: 1, final: 2 };
  const best = new Map<string, PipelineRow>();
  for (const row of rows) {
    const held = best.get(row.opportunityId);
    if (!held || rank[row.round] > rank[held.round]) best.set(row.opportunityId, row);
  }
  return [...best.values()];
}

/**
 * Soonest first, everyone with nothing booked after them.
 *
 * Same rule the three separate lists used: the rows carrying a date are the ones a recruiter
 * scans, and "waiting on the candidate" has no date to sort by.
 */
function sortRows(rows: PipelineRow[]): PipelineRow[] {
  return rows.slice().sort((a, b) => {
    if (a.start && b.start) return Date.parse(a.start) - Date.parse(b.start);
    if (a.start) return -1;
    if (b.start) return 1;
    return a.name.localeCompare(b.name);
  });
}

/**
 * The header's counts.
 *
 * `missing` is what opens the strip by itself, so it counts only the state that needs a person —
 * a booking with no Lever interview behind it.
 */
export function pipelineSummary(rows: PipelineRow[]): {
  total: number;
  booked: number;
  waiting: number;
  missing: number;
} {
  return {
    total: rows.length,
    booked: rows.filter((r) => r.state !== 'invited').length,
    waiting: rows.filter((r) => r.state === 'invited').length,
    /**
     * NOT simply `!inLever`. An INVITED candidate has booked nothing, so there is no interview
     * for Lever to be missing — counting them would open the strip by itself and send a
     * recruiter to create a panel for a round nobody has scheduled.
     */
    missing: rows.filter((r) => r.state !== 'invited' && !r.inLever).length,
  };
}

/** "9 in flight · 9 booked" — the booked half only when there is one. */
export function pipelineLabel(rows: PipelineRow[]): string {
  const { total, booked } = pipelineSummary(rows);
  return `In flight — ${total}${booked > 0 ? `, ${booked} booked` : ''}`;
}

/** "Invited 3 Oct by anna.thiel" — the day and the sender, never the whole timestamp. */
export function invitedLine(row: PipelineRow): string | null {
  if (!row.invitedAt) return null;
  const when = new Date(row.invitedAt);
  if (Number.isNaN(when.getTime())) return null;
  const day = when.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
  const who = row.invitedBy ? ` by ${row.invitedBy.split('@')[0]}` : '';
  return `Invited ${day}${who}`;
}

/** What the row and the dialog say about an interviewer's no-show signal. */
/**
 * What to say about the attempts BEFORE the booking on the row.
 *
 * Both dates on screen, which is the whole point: a recruiter looking at "booked Thu 10:00"
 * cannot otherwise tell that Tuesday's interview was missed, and may ask the candidate to book
 * all over again (Theresa, Oct 22 2026).
 */
export function earlierLine(
  earlier: { start: string; noShow: boolean }[],
  fmt: (iso: string) => string
): string | null {
  if (!earlier || earlier.length === 0) return null;
  const [first, ...rest] = earlier;
  const what = first.noShow ? 'a no-show' : 'an earlier slot';
  const more = rest.length > 0 ? ` (+${rest.length} before that)` : '';
  return `after ${what} on ${fmt(first.start)}${more}`;
}

export function noShowLine(n: { kind: 'reported' | 'dismissed'; by: string | null }): string {
  const who = n.by || 'The interviewer';
  return n.kind === 'reported'
    ? `${who} reported a no-show in the Lever feedback.`
    : `${who} dismissed the Lever feedback request for this interview — possibly a no-show.`;
}
